import React, { Fragment, useMemo, useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useQuery, useQueries, useMutation, useQueryClient } from 'react-query';
import { getAdminTenantId } from '../utils/tenantHelper';
import { adminEventTournamentsAPI } from '../services/adminApi';
import SeasonFormFieldsShared from './admin/SeasonFormFieldsShared';
import {
  buildSeasonApiPayload,
  getDefaultSeasonFormInCampaign,
  parseRegions,
  toLocalDatetimeInput,
} from '../utils/seasonFormUtils';
import AdminRegistrationPageSettings from './AdminRegistrationPageSettings';

const LIVE_SEASON_FOR_PUBLISH = new Set([
  'active',
  'upcoming',
  'registration',
  'registration_open',
]);

const Container = styled.div`
  padding: 1rem;
  max-width: 1280px;
  margin: 0 auto;
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  align-items: start;

  @media (min-width: 1024px) {
    grid-template-columns: minmax(300px, 380px) minmax(0, 1fr);
    gap: 1.25rem;
  }
`;

const SidebarColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const WorkspaceColumn = styled.div`
  min-width: 0;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  margin-bottom: 1rem;
`;

const WorkspaceCard = styled(Card)`
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
  margin-bottom: 0;
`;

const WorkspaceToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f1f5f9;
`;

const WorkspaceTitleBlock = styled.div`
  min-width: 0;
  flex: 1;
`;

const WorkspaceKicker = styled.p`
  margin: 0 0 0.2rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${(p) => p.$muted || '#043663'};
`;

const WorkspaceH2 = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
  word-break: break-word;
`;

const StatusChips = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
  margin-top: 0.45rem;
`;

const StatusChip = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.22rem 0.55rem;
  border-radius: 6px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
`;

const EmptyWorkspace = styled.div`
  background: #fafafa;
  border: 2px dashed #e2e8f0;
  border-radius: 12px;
  padding: 2rem 1.5rem;
  text-align: center;
  color: #64748b;

  p {
    margin: 0 0 1rem;
    font-size: 0.9375rem;
    line-height: 1.55;
    max-width: 44ch;
    margin-left: auto;
    margin-right: auto;
  }
`;

const Title = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.25rem 0;
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0 0 1.25rem 0;
`;

const SectionLabel = styled.h4`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #374151;
  margin: 1.5rem 0 0.75rem 0;
  padding-bottom: 0.35rem;
  border-bottom: 1px solid #e5e7eb;
  &:first-of-type {
    margin-top: 0;
  }
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr 1fr;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  ${(p) => p.$fullWidth && 'grid-column: 1 / -1;'}
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 2px rgba(4, 54, 99, 0.15);
  }
`;

const TextArea = styled.textarea`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  &:focus {
    outline: none;
    border-color: #043663;
  }
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9375rem;
  background: white;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const Button = styled.button`
  padding: 0.5rem 1.25rem;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  background: #043663;
  color: white;
  &:hover {
    background: #032a4a;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonSecondary = styled(Button)`
  background: #f3f4f6;
  color: #374151;
  &:hover {
    background: #e5e7eb;
  }
`;

const ButtonGoLive = styled(Button)`
  background: #059669;
  &:hover {
    background: #047857;
  }
`;

const ButtonDanger = styled(Button)`
  background: #dc2626;
  &:hover {
    background: #b91c1c;
  }
`;

const DraftList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DraftItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.65rem 0.85rem;
  background: ${(p) => (p.$selected ? '#eff6ff' : '#f9fafb')};
  border-radius: 8px;
  border: 1px solid ${(p) => (p.$selected ? '#93c5fd' : '#e5e7eb')};
  flex-wrap: wrap;
  box-shadow: ${(p) => (p.$selected ? '0 0 0 2px rgba(37,99,235,0.15)' : 'none')};
  cursor: ${(p) => (p.$interactive === false ? 'default' : 'pointer')};
  transition:
    border-color 0.15s,
    background 0.15s;

  &:hover {
    border-color: ${(p) => (p.$selected ? '#60a5fa' : '#d1d5db')};
  }
`;

const DraftInfo = styled.span`
  font-size: 0.9375rem;
  color: #374151;
  flex: 1;
  min-width: 120px;
`;

const DraftActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const SeasonsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const SeasonPill = styled.button`
  border: 1px solid ${(p) => (p.$selected ? '#043663' : '#d1d5db')};
  background: ${(p) => (p.$selected ? '#e6f0f7' : '#fff')};
  color: #1f2937;
  padding: 0.4rem 0.7rem;
  border-radius: 999px;
  font-size: 0.8rem;
  cursor: pointer;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const Hint = styled.span`
  font-size: 0.75rem;
  color: #9ca3af;
  font-weight: 400;
`;

const PageIntro = styled.div`
  margin-bottom: 1.25rem;
`;

const PageKicker = styled.p`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #043663;
  margin: 0 0 0.35rem 0;
`;

const PageHeading = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.5rem 0;
  line-height: 1.25;
`;

const PageLead = styled.p`
  font-size: 0.9375rem;
  color: #475569;
  line-height: 1.55;
  margin: 0;
  max-width: 62ch;
`;

const GuideCard = styled.aside`
  background: linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%);
  border: 1px solid #dbeafe;
  border-radius: 12px;
  padding: 1.25rem 1.35rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
`;

const GuideTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.45rem;
`;

const GuideOl = styled.ol`
  counter-reset: guide;
  list-style: none;
  padding: 0;
  margin: 0 0 1rem 0;
  display: grid;
  gap: 0.65rem;

  li {
    counter-increment: guide;
    position: relative;
    padding-left: 2rem;
    font-size: 0.875rem;
    line-height: 1.5;
    color: #334155;
  }
  li::before {
    content: counter(guide);
    position: absolute;
    left: 0;
    top: 0.1rem;
    width: 1.4rem;
    height: 1.4rem;
    border-radius: 999px;
    background: #043663;
    color: white;
    font-size: 0.7rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const GlossaryDl = styled.dl`
  margin: 0;
  padding: 0.85rem 0 0;
  border-top: 1px solid #dbeafe;

  dt {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0.65rem 0 0.2rem;

    &:first-of-type {
      margin-top: 0;
    }
  }
  dd {
    margin: 0;
    font-size: 0.8125rem;
    line-height: 1.5;
    color: #64748b;
  }
`;

const SectionHelp = styled.p`
  font-size: 0.8125rem;
  color: #64748b;
  line-height: 1.5;
  margin: -0.25rem 0 0.85rem;
  max-width: 68ch;

  ${(p) => p.$muted && `color: #94a3b8;`}
`;

const ListCardTitle = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const StatusLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  font-size: 0.75rem;
  color: #64748b;

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }
`;

const Dot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: ${(p) => p.$c || '#94a3b8'};
`;

const SeasonTips = styled.div`
  font-size: 0.8125rem;
  line-height: 1.55;
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.9rem 1rem;
  margin-bottom: 1rem;

  ul {
    margin: 0.5rem 0 0 1.1rem;
    padding: 0;
  }
  li {
    margin: 0.25rem 0;
  }
`;

const SeasonBlockTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.35rem 0;
`;

const SettingsTabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid #e2e8f0;
`;

const SettingsTab = styled.button`
  appearance: none;
  border: 1px solid ${(p) => (p.$active ? '#043663' : '#cbd5e1')};
  background: ${(p) => (p.$active ? '#043663' : '#fff')};
  color: ${(p) => (p.$active ? '#fff' : '#334155')};
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.55rem 0.95rem;
  border-radius: 8px;
  cursor: pointer;
  &:hover {
    border-color: #043663;
  }
`;

/** Season builder UI shows essentials only; keep unused keys so edit save does not wipe DB values. */
const defaultForm = () => ({
  title: '',
  description: '',
  start_date: '',
  end_date: '',
  registration_start_date: '',
  registration_end_date: '',
  entry_fee_chf: '',
  format: 'single_elimination',
  status: 'draft',
  venue_name: '',
  venue_address: '',
  contact_email: '',
  contact_phone: '',
  prize_info: '',
  skill_level_note: '',
  banner_image_url: '',
  waitlist_enabled: false,
  public_visible: true,
  allow_late_registration: false,
  internal_notes: '',
  migration_bundle: false,
  registration_closed_title: '',
  registration_closed_body: '',
});

function formatDraftDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '';
  }
}

function campaignStatusLabel(status, campaignCompleted = false) {
  if (campaignCompleted) return 'Completed';
  if (status === 'published') return 'Live';
  if (status === 'discontinued') return 'Paused';
  return 'Draft';
}

/** Map linked season row (GET …/event-tournaments/:id/seasons) to campaign season edit form */
function linkedSeasonRowToEditForm(ls) {
  return {
    name: ls.season_name || '',
    start_date: toLocalDatetimeInput(ls.start_date),
    end_date: toLocalDatetimeInput(ls.end_date),
    registration_start: toLocalDatetimeInput(ls.registration_start),
    registration_end: toLocalDatetimeInput(ls.registration_end),
    registration_fee: ls.registration_fee != null ? String(ls.registration_fee) : '',
    currency: ls.currency || 'CHF',
    regions_allowed: parseRegions(ls.regions_allowed),
    score_submission_locking_enabled: ls.score_submission_locking_enabled !== false,
    skill_level_edit_deadline: toLocalDatetimeInput(ls.skill_level_edit_deadline),
    lock_skill_level_when_registration_closed: ls.lock_skill_level_when_registration_closed !== false,
    status: ls.status || 'draft',
    activate_now: true,
  };
}

function rowToForm(row) {
  const meta = row.admin_metadata || {};
  return {
    title: row.title || '',
    description: row.description || '',
    start_date: row.start_date ? String(row.start_date).slice(0, 10) : '',
    end_date: row.end_date ? String(row.end_date).slice(0, 10) : '',
    registration_start_date: row.registration_start_date ? String(row.registration_start_date).slice(0, 10) : '',
    registration_end_date: row.registration_end_date ? String(row.registration_end_date).slice(0, 10) : '',
    entry_fee_chf: row.entry_fee_chf != null ? String(row.entry_fee_chf) : '',
    format: row.format || meta.format || 'single_elimination',
    status: row.status || 'draft',
    venue_name: row.venue_name || '',
    venue_address: row.venue_address || '',
    contact_email: row.contact_email || '',
    contact_phone: row.contact_phone ?? meta.contact_phone ?? '',
    prize_info: row.prize_info ?? meta.prize_info ?? '',
    skill_level_note: row.skill_level_note ?? meta.skill_level_note ?? '',
    banner_image_url: row.banner_image_url || '',
    waitlist_enabled: row.waitlist_enabled !== undefined ? row.waitlist_enabled : meta.waitlist_enabled === true,
    public_visible: row.public_visible !== undefined ? row.public_visible : meta.public_visible !== false,
    allow_late_registration: row.allow_late_registration !== undefined ? row.allow_late_registration : meta.allow_late_registration === true,
    internal_notes: row.internal_notes ?? meta.internal_notes ?? '',
    migration_bundle: meta.migration_bundle === true,
    registration_closed_title: row.registration_closed_title || '',
    registration_closed_body: row.registration_closed_body || '',
  };
}

const AdminSettingsTournaments = () => {
  const tenantId = getAdminTenantId();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(defaultForm);
  /** `null` closed panel; `'new'` composing unsaved draft; else saved campaign id */
  const [activeCampaignId, setActiveCampaignId] = useState(null);
  const editingId =
    activeCampaignId && activeCampaignId !== 'new' ? activeCampaignId : null;
  const isNewCampaignFlow = activeCampaignId === 'new';
  const [publishingId, setPublishingId] = useState(null);
  const [discontinuingId, setDiscontinuingId] = useState(null);
  const [selectedSeasonIds, setSelectedSeasonIds] = useState([]);
  const [seasonForm, setSeasonForm] = useState(() => getDefaultSeasonFormInCampaign());
  const [editingLinkedSeasonId, setEditingLinkedSeasonId] = useState(null);
  const [editSeasonForm, setEditSeasonForm] = useState(() => getDefaultSeasonFormInCampaign());
  const [listFilter, setListFilter] = useState('all');
  const [copySourceSeasonId, setCopySourceSeasonId] = useState('');
  const [copyTargetSeasonId, setCopyTargetSeasonId] = useState('');
  const [settingsTab, setSettingsTab] = useState('campaigns'); // 'campaigns' | 'registration_page'

  const { data: listRes } = useQuery(
    ['adminEventTournaments', tenantId],
    () => adminEventTournamentsAPI.getAll(),
    { enabled: !!tenantId }
  );
  const list = useMemo(
    () => (Array.isArray(listRes?.data) ? listRes.data : []),
    [listRes]
  );
  const { data: seasonsRes } = useQuery(
    ['adminEventTournamentsSeasons', tenantId],
    () => adminEventTournamentsAPI.getTenantSeasons(),
    { enabled: !!tenantId }
  );
  const tenantSeasons = Array.isArray(seasonsRes?.data) ? seasonsRes.data : [];

  const { data: linkedRes } = useQuery(
    ['adminEventTournamentLinkedSeasons', editingId],
    () => adminEventTournamentsAPI.getLinkedSeasons(editingId),
    { enabled: !!editingId }
  );
  const linkedSeasons = useMemo(
    () => (Array.isArray(linkedRes?.data) ? linkedRes.data : []),
    [linkedRes]
  );
  const linkedSeasonIdSet = useMemo(
    () => new Set((linkedSeasons || []).map((s) => String(s.season_id || s.id))),
    [linkedSeasons]
  );
  const allCampaigns = [...list].sort((a, b) => {
    const da = new Date(a.updated_at || a.created_at || 0).getTime();
    const db = new Date(b.updated_at || b.created_at || 0).getTime();
    return db - da;
  });

  const draftCampaignIds = useMemo(
    () => list.filter((c) => c.status === 'draft').map((c) => c.id),
    [list]
  );

  const draftLinkedPrefetch = useQueries(
    draftCampaignIds.map((campaignId) => ({
      queryKey: ['adminEventTournamentLinkedSeasons', campaignId],
      queryFn: () => adminEventTournamentsAPI.getLinkedSeasons(campaignId),
      enabled: !!tenantId && draftCampaignIds.length > 0,
    }))
  );

  const draftPublishStateById = useMemo(() => {
    const map = {};
    draftCampaignIds.forEach((id, idx) => {
      const qr = draftLinkedPrefetch[idx];
      const loading = qr?.isLoading || qr?.isFetching;
      if (loading) {
        map[id] = { loading: true };
        return;
      }
      const seasons = Array.isArray(qr?.data?.data) ? qr.data.data : [];
      const norm = (st) => String(st || '').toLowerCase();
      const hasLiveLike = seasons.some((s) => LIVE_SEASON_FOR_PUBLISH.has(norm(s.status)));
      const row = list.find((c) => c.id === id);
      const meta = row?.admin_metadata || {};
      const mig = meta.migration_bundle === true;
      const allLinkedCompleted =
        seasons.length > 0 && seasons.every((s) => norm(s.status) === 'completed');
      let reason = null;
      if (seasons.length === 0) {
        reason = 'Link at least one phase before going live.';
      } else if (!hasLiveLike && !(mig && allLinkedCompleted)) {
        reason = mig
          ? 'Migration bundle: every linked phase must be completed before publish without an active/upcoming phase.'
          : 'At least one linked phase must be upcoming, active, or in registration (publish rule matches the server).';
      }
      map[id] = { loading: false, reason };
    });
    return map;
  }, [draftCampaignIds, draftLinkedPrefetch, list]);

  const filteredCampaigns = useMemo(() => {
    if (listFilter === 'all') return allCampaigns;
    return allCampaigns.filter((c) => {
      if (listFilter === 'draft') return c.status === 'draft';
      if (listFilter === 'live') return c.status === 'published' && !c.campaign_completed;
      if (listFilter === 'completed') return !!c.campaign_completed;
      if (listFilter === 'paused') return c.status === 'discontinued' && !c.campaign_completed;
      return true;
    });
  }, [allCampaigns, listFilter]);

  const createMutation = useMutation(
    (body) => adminEventTournamentsAPI.create(body),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(['adminEventTournaments']);
        // adminApi interceptor returns the JSON body: { success, data: { id, ... } }
        const newId = res?.data?.id;
        if (newId) setActiveCampaignId(newId);
        toast.success('Draft saved. Add phases in the workspace.');
      },
      onError: (e) => toast.error(e.response?.data?.message || e.message || 'Save failed'),
    }
  );
  const updateMutation = useMutation(
    ({ id, body }) => adminEventTournamentsAPI.update(id, body),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries(['adminEventTournaments']);
        queryClient.invalidateQueries(['adminEventTournamentLinkedSeasons', id]);
        toast.success('Saved.');
      },
      onError: (e) => toast.error(e.response?.data?.message || e.message || 'Update failed'),
    }
  );
  const publishMutation = useMutation(
    (id) => adminEventTournamentsAPI.publish(id),
    {
      onSuccess: (_, id) => {
        queryClient.invalidateQueries(['adminEventTournaments']);
        queryClient.invalidateQueries(['adminEventTournamentLinkedSeasons', id]);
        queryClient.invalidateQueries(['adminEventTournamentLinkedSeasons']);
        setPublishingId(null);
        toast.success('Tournament is live for players.');
      },
      onError: (e) => {
        setPublishingId(null);
        toast.error(e.response?.data?.message || e.message || 'Publish failed');
      },
    }
  );
  const republishMutation = useMutation(
    (id) => adminEventTournamentsAPI.republish(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['adminEventTournaments']);
        toast.success('Season is live again for players.');
      },
      onError: (e) => toast.error(e.response?.data?.message || e.message || 'Go live again failed'),
    }
  );
  const discontinueMutation = useMutation(
    (id) => adminEventTournamentsAPI.discontinue(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['adminEventTournaments']);
        setDiscontinuingId(null);
        toast.success('Season paused. It stays listed here — use "Go live again" when ready.');
      },
      onError: (e) => {
        setDiscontinuingId(null);
        toast.error(e.response?.data?.message || e.message || 'Discontinue failed');
      },
    }
  );
  const deleteMutation = useMutation(
    (id) => adminEventTournamentsAPI.delete(id),
    {
      onSuccess: (_, deletedId) => {
        queryClient.invalidateQueries(['adminEventTournaments']);
        setActiveCampaignId((current) => (current === deletedId ? null : current));
        toast.success('Draft deleted.');
      },
      onError: (e) => toast.error(e.response?.data?.message || e.message || 'Delete failed'),
    }
  );
  const linkSeasonsMutation = useMutation(
    ({ id, seasonIds }) => adminEventTournamentsAPI.linkSeasons(id, seasonIds),
    {
      onSuccess: (_, { id: campaignId }) => {
        queryClient.invalidateQueries(['adminEventTournamentLinkedSeasons', campaignId]);
        queryClient.invalidateQueries(['adminEventTournamentLinkedSeasons']);
        queryClient.invalidateQueries(['adminEventTournamentsSeasons', tenantId]);
        toast.success('Season phases updated.');
      },
      onError: (e) => toast.error(e.response?.data?.message || e.message || 'Link phases failed'),
    }
  );

  const uploadBannerMutation = useMutation(
    ({ id, file }) => adminEventTournamentsAPI.uploadBanner(id, file),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['adminEventTournaments', tenantId]);
        toast.success('Banner image saved to database.');
      },
      onError: (e) => toast.error(e.response?.data?.message || e.message || 'Banner upload failed'),
    }
  );

  const createSeasonInCampaignMutation = useMutation(
    ({ id, body }) => adminEventTournamentsAPI.createSeasonInCampaign(id, body),
    {
      onSuccess: (res, { id: campaignId }) => {
        queryClient.invalidateQueries(['adminEventTournamentLinkedSeasons', campaignId]);
        queryClient.invalidateQueries(['adminEventTournamentLinkedSeasons']);
        queryClient.invalidateQueries(['adminEventTournamentsSeasons', tenantId]);
        queryClient.invalidateQueries(['admin-seasons']);
        setSeasonForm(getDefaultSeasonFormInCampaign());
        const copy = res?.data?.divisions_copy;
        if (copy?.auto_copied && copy.divisions_created > 0) {
          toast.success(
            `Phase created. Copied ${copy.divisions_created} divisions and ${copy.assignments_copied || 0} team placements from the previous phase.`
          );
        } else if (res?.data?.divisions_copy_error) {
          toast.error(res.message || 'Phase created but division copy failed — use Copy divisions manually.');
        } else {
          toast.success(
            res?.message ||
              'Phase created inside season. First phase uses shared skill bands; build group divisions in League Management.'
          );
        }
      },
      onError: (e) => {
        // Phase may already exist if auto-copy failed after insert
        queryClient.invalidateQueries(['adminEventTournamentLinkedSeasons']);
        queryClient.invalidateQueries(['adminEventTournamentsSeasons', tenantId]);
        queryClient.invalidateQueries(['admin-seasons']);
        toast.error(e.response?.data?.message || e.message || 'Phase creation failed');
      },
    }
  );

  const updateSeasonViaCampaignMutation = useMutation(
    ({ campaignId, seasonId, body }) => adminEventTournamentsAPI.updateLinkedSeason(campaignId, seasonId, body),
    {
      onSuccess: (res, { campaignId }) => {
        queryClient.invalidateQueries(['adminEventTournamentLinkedSeasons', campaignId]);
        queryClient.invalidateQueries(['adminEventTournamentsSeasons', tenantId]);
        setEditingLinkedSeasonId(null);
        const s = res?.data?.season;
        if (s && (s.status != null || s.end_date != null)) {
          const endBit = s.end_date ? String(s.end_date).slice(0, 16) : '';
          toast.success(`Phase saved — status: ${s.status || '?'}` + (endBit ? ` · end ${endBit}` : ''));
        } else {
          toast.success('Phase updated.');
        }
      },
      onError: (e) => toast.error(e.response?.data?.message || e.message || 'Phase update failed'),
    }
  );

  const copyDivisionsMutation = useMutation(
    ({ campaignId, source_season_id, target_season_id }) =>
      adminEventTournamentsAPI.copyDivisionsBetweenSeasons(campaignId, {
        source_season_id,
        target_season_id,
      }),
    {
      onSuccess: (_, { campaignId }) => {
        queryClient.invalidateQueries(['adminEventTournamentLinkedSeasons', campaignId]);
        queryClient.invalidateQueries(['adminEventTournamentsSeasons', tenantId]);
        toast.success('Divisions and assignments copied.');
      },
      onError: (e) => toast.error(e.response?.data?.message || e.message || 'Copy failed'),
    }
  );

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const editingRow = editingId ? list.find((r) => r.id === editingId) : null;
  const editingStatus = editingRow?.status;

  const handleSave = () => {
    if (!tenantId) {
      toast.error('Select a league in the admin bar first.');
      return;
    }
    if (!form.title.trim()) {
      toast.error('Please enter a season title');
      return;
    }
    // Send full form (including fields hidden in UI) so updates do not null existing DB values.
    if (editingId) {
      const { status: _omit, ...bodyWithoutStatus } = form;
      updateMutation.mutate({ id: editingId, body: bodyWithoutStatus });
    } else {
      createMutation.mutate(form);
    }
  };

  /** Fresh unsaved campaign row: config + seasons after first save (`activeCampaignId` → real id). */
  const startNewCampaign = () => {
    setForm(defaultForm());
    setActiveCampaignId('new');
    setSelectedSeasonIds([]);
    setSeasonForm(getDefaultSeasonFormInCampaign());
    setEditingLinkedSeasonId(null);
    setCopySourceSeasonId('');
    setCopyTargetSeasonId('');
  };

  const handleDeleteDraft = (draft) => {
    if (!window.confirm(`Delete draft "${draft.title}"?`)) return;
    deleteMutation.mutate(draft.id);
  };

  const handleGoLive = (draft) => {
    if (!window.confirm(`Go live with "${draft.title}"?`)) return;
    setPublishingId(draft.id);
    publishMutation.mutate(draft.id);
  };

  const handleDiscontinue = (item) => {
    if (!window.confirm(`Pause "${item.title}"? Players will no longer see it until you go live again.`)) return;
    setDiscontinuingId(item.id);
    discontinueMutation.mutate(item.id);
  };

  const handleRepublish = (item) => {
    if (!window.confirm(`Go live again with "${item.title}"?`)) return;
    republishMutation.mutate(item.id);
  };

  const handleLoadDraft = (draft) => {
    const row = draft.row || draft;
    const id = draft.id ?? row.id;
    setForm(rowToForm(row));
    setActiveCampaignId(id);
    setSelectedSeasonIds([]);
    setEditingLinkedSeasonId(null);
    setCopySourceSeasonId('');
    setCopyTargetSeasonId('');
    toast.success('Opened in the workspace →');
  };

  /** Close the right-hand workspace and reset local form state */
  const closeWorkspace = () => {
    setActiveCampaignId(null);
    setForm(defaultForm());
    setSelectedSeasonIds([]);
    setSeasonForm(getDefaultSeasonFormInCampaign());
    setEditingLinkedSeasonId(null);
    setCopySourceSeasonId('');
    setCopyTargetSeasonId('');
  };

  const toggleSeason = (seasonId) => {
    setSelectedSeasonIds((prev) =>
      prev.includes(seasonId) ? prev.filter((id) => id !== seasonId) : [...prev, seasonId]
    );
  };

  const handleSaveLinkedSeasons = () => {
    if (!editingId) {
      toast.error('Load a season draft first.');
      return;
    }
    if (editingRow?.campaign_completed) {
      toast.error(
        'This season is completed. Create a new season for the next edition instead of linking more phases here.'
      );
      return;
    }
    if (selectedSeasonIds.length === 0) {
      toast.error('Select at least one phase.');
      return;
    }
    linkSeasonsMutation.mutate({ id: editingId, seasonIds: selectedSeasonIds });
  };

  const handleUnlinkSeason = async (seasonId) => {
    try {
      await adminEventTournamentsAPI.unlinkSeason(editingId, seasonId);
      queryClient.invalidateQueries(['adminEventTournamentLinkedSeasons', editingId]);
      queryClient.invalidateQueries(['adminEventTournamentsSeasons', tenantId]);
      toast.success('Phase disabled in season.');
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Unlink failed');
    }
  };

  const handleCreateSeasonInCampaign = () => {
    if (!editingId) {
      toast.error('Load or create a season first.');
      return;
    }
    if (editingRow?.campaign_completed) {
      toast.error(
        'This season is completed. Create a new season (edition) for the next phase instead of adding phases here.'
      );
      return;
    }
    let body;
    try {
      const base = buildSeasonApiPayload(seasonForm);
      body = { ...base, activate_now: seasonForm.activate_now !== false };
    } catch (e) {
      toast.error(e.message || 'Validation failed');
      return;
    }
    createSeasonInCampaignMutation.mutate({ id: editingId, body });
  };

  const handleSaveEditedLinkedSeason = () => {
    if (!editingId || !editingLinkedSeasonId) return;
    let body;
    try {
      body = { ...buildSeasonApiPayload(editSeasonForm), status: editSeasonForm.status };
    } catch (e) {
      toast.error(e.message || 'Validation failed');
      return;
    }
    updateSeasonViaCampaignMutation.mutate({ campaignId: editingId, seasonId: editingLinkedSeasonId, body });
  };

  return (
    <Container>
      <SettingsTabBar>
        <SettingsTab
          type="button"
          $active={settingsTab === 'campaigns'}
          onClick={() => setSettingsTab('campaigns')}
        >
          Seasons
        </SettingsTab>
        <SettingsTab
          type="button"
          $active={settingsTab === 'registration_page'}
          onClick={() => setSettingsTab('registration_page')}
        >
          Registration page settings
        </SettingsTab>
      </SettingsTabBar>

      {settingsTab === 'registration_page' ? (
        <AdminRegistrationPageSettings />
      ) : (
        <>
      <PageIntro>
        <PageKicker>Season manager</PageKicker>
        <PageHeading>Leagues, phases & go-live checks</PageHeading>
        <PageLead>
          Bundle phases under a player-facing season (title, story, banner). Matches and standings stay on phase IDs —
          set dates and fees on each phase. Follow the guide for <strong>last phase</strong> and publishing.
        </PageLead>
      </PageIntro>

      <GuideCard>
        <GuideTitle>Quick guide</GuideTitle>
        <GuideOl>
          <li>
            Create a season with <strong>title</strong>, <strong>description</strong>, and optional{' '}
            <strong>banner</strong>. Publish from the list when ready.
          </li>
          <li>
            <strong>Link or create phases</strong> under the season — phase dates and fees control registration.
          </li>
          <li>
            Mark <strong>one</strong> linked phase as <strong>Last phase of season</strong>. When that phase is{' '}
            <em>completed</em>, the season is treated as finished for players.
          </li>
          <li>
            Use <strong>Carry divisions forward</strong> when a new phase needs the same division tree as a previous one.
          </li>
        </GuideOl>
        <GlossaryDl>
          <dt>Season</dt>
          <dd>Player-facing title, description, and banner. Wraps one or more phases.</dd>
          <dt>Phase</dt>
          <dd>League period with dates, fee, standings, and registrations.</dd>
          <dt>Pause vs delete</dt>
          <dd>Pause hides a live season; drafts can be deleted. Phases stay in the league unless unlinked.</dd>
        </GlossaryDl>
      </GuideCard>

      <MainGrid>
        <SidebarColumn>
          <Card>
            <div
              style={{
                marginBottom: '1rem',
                paddingBottom: '1rem',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <Button type="button" onClick={startNewCampaign} style={{ width: '100%' }}>
                + New season
              </Button>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem', color: '#64748b', lineHeight: 1.45 }}>
                Opens an empty workspace on the right — save once, then add phases there.
              </p>
            </div>
            <ListCardTitle>
          <Title style={{ margin: 0 }}>Your seasons</Title>
          <StatusLegend aria-hidden="true">
            <span><Dot $c="#9ca3af" />Draft</span>
            <span><Dot $c="#059669" />Live</span>
            <span><Dot $c="#64748b" />Completed</span>
            <span><Dot $c="#eab308" />Paused</span>
          </StatusLegend>
        </ListCardTitle>
        <Subtitle style={{ marginBottom: '0.75rem' }}>
          Click a row to load it into the workspace, or create new above. Actions on each row (Go live, Pause) stay here — editing copy and phases happens on the right.
        </Subtitle>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <Label htmlFor="campaign-list-filter" style={{ marginBottom: 0 }}>
            Filter
          </Label>
          <Select
            id="campaign-list-filter"
            value={listFilter}
            onChange={(e) => setListFilter(e.target.value)}
            style={{ minWidth: '180px' }}
            aria-label="Filter seasons by status"
          >
            <option value="all">All</option>
            <option value="draft">Draft only</option>
            <option value="live">Live only</option>
            <option value="completed">Completed only</option>
            <option value="paused">Paused only</option>
          </Select>
        </div>
        <DraftList>
          {filteredCampaigns.map((c) => {
            const rowBundle = { id: c.id, title: c.title, row: c };
            const savedAt = c.updated_at || c.created_at;
            const endHint =
              c.end_date && c.status !== 'draft' ? (
                <span style={{ color: '#6b7280', fontSize: '0.8125rem' }}>
                  {' · End '}
                  {String(c.end_date).slice(0, 10)}
                </span>
              ) : null;
            const pub = draftPublishStateById[c.id];
            const goLiveBlocked =
              c.status === 'draft' &&
              (pub?.loading ||
                !!(pub && !pub.loading && pub.reason != null && pub.reason !== ''));
            return (
              <DraftItem
                key={c.id}
                $selected={String(activeCampaignId) === String(c.id)}
                $interactive
                onClick={() => handleLoadDraft(rowBundle)}
                style={
                  c.status === 'draft' && pub?.reason && !pub.loading
                    ? { borderLeftWidth: 3, borderLeftStyle: 'solid', borderLeftColor: '#f59e0b' }
                    : undefined
                }
              >
                <DraftInfo>
                  <strong>{c.title}</strong>
                  {' · '}
                  <span style={{ color: '#6b7280', fontSize: '0.8125rem' }}>
                    {campaignStatusLabel(c.status, c.campaign_completed)}
                  </span>
                  {c.status === 'draft' ? (
                    <span style={{ color: '#6b7280', fontSize: '0.8125rem' }}>
                      {' · '}
                      {formatDraftDate(savedAt)}
                    </span>
                  ) : (
                    endHint
                  )}
                  {c.status === 'draft' && pub?.reason && !pub.loading && (
                    <div style={{ flexBasis: '100%', fontSize: '0.75rem', color: '#b45309', marginTop: '0.25rem' }}>
                      {pub.reason}
                    </div>
                  )}
                  {c.campaign_completed && (
                    <div style={{ flexBasis: '100%', fontSize: '0.75rem', color: '#475569', marginTop: '0.25rem' }}>
                      Last linked phase completed — open a new season for the next edition.
                    </div>
                  )}
                </DraftInfo>
                <DraftActions onClick={(e) => e.stopPropagation()}>
                  <ButtonSecondary
                    type="button"
                    onClick={() => handleLoadDraft(rowBundle)}
                    title="Open this season in the workspace on the right to edit copy, phases, or links"
                  >
                    Edit in builder
                  </ButtonSecondary>
                  {c.status === 'draft' && (
                    <>
                      <ButtonGoLive
                        type="button"
                        disabled={publishingId === c.id || goLiveBlocked}
                        onClick={() => handleGoLive(rowBundle)}
                        title={pub?.reason ? `Cannot publish: ${pub.reason}` : 'Publish this draft for players'}
                      >
                        {publishingId === c.id ? 'Publishing…' : pub?.loading ? 'Checking publish rules…' : 'Go live'}
                      </ButtonGoLive>
                      <ButtonDanger type="button" onClick={() => handleDeleteDraft(rowBundle)}>
                        Delete
                      </ButtonDanger>
                    </>
                  )}
                  {c.status === 'published' && !c.campaign_completed && (
                    <ButtonDanger
                      type="button"
                      disabled={discontinuingId === c.id}
                      onClick={() => handleDiscontinue(rowBundle)}
                    >
                      {discontinuingId === c.id ? 'Discontinuing…' : 'Pause'}
                    </ButtonDanger>
                  )}
                  {c.status === 'discontinued' && !c.campaign_completed && (
                    <ButtonGoLive
                      type="button"
                      disabled={republishMutation.isLoading}
                      onClick={() => handleRepublish(rowBundle)}
                    >
                      {republishMutation.isLoading ? 'Publishing…' : 'Go live again'}
                    </ButtonGoLive>
                  )}
                </DraftActions>
              </DraftItem>
            );
          })}
          {filteredCampaigns.length === 0 && (
            <Subtitle style={{ padding: '1rem 0' }}>
              {allCampaigns.length === 0
                ? 'No seasons yet — use + New season on the left, save a draft, then add phases in the workspace.'
                : 'No seasons match this filter — try All or pick another status.'}
            </Subtitle>
          )}
        </DraftList>
      </Card>
        </SidebarColumn>

        <WorkspaceColumn>
          {activeCampaignId === null ? (
            <EmptyWorkspace>
              <h3 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem', fontWeight: 600, color: '#334155' }}>Select or create</h3>
              <p>
                Use <strong>+ New season</strong> on the left to start fresh, or click a season row (or <strong>Edit in builder</strong>) to
                load copy and phases here.
              </p>
            </EmptyWorkspace>
          ) : (
            <WorkspaceCard>
              <WorkspaceToolbar>
                <WorkspaceTitleBlock>
                  <WorkspaceKicker $muted={isNewCampaignFlow ? '#64748b' : undefined}>
                    {isNewCampaignFlow ? 'Create' : 'Edit season'}
                  </WorkspaceKicker>
                  <WorkspaceH2>
                    {isNewCampaignFlow
                      ? 'New season'
                      : form.title?.trim() ||
                        allCampaigns.find((d) => String(d.id) === String(editingId))?.title ||
                        'Untitled'}
                  </WorkspaceH2>
                  {!isNewCampaignFlow && editingRow && (
                    <StatusChips>
                      <StatusChip $bg="#f1f5f9" $color="#334155">
                        {campaignStatusLabel(editingRow.status, editingRow.campaign_completed)}
                      </StatusChip>
                      {editingRow.campaign_completed ? (
                        <StatusChip $bg="#e2e8f0" $color="#334155">Completed</StatusChip>
                      ) : editingRow.status === 'draft' ? (
                        <StatusChip $bg="#fef3c7" $color="#92400e">Not live yet</StatusChip>
                      ) : editingRow.status === 'published' ? (
                        <StatusChip $bg="#d1fae5" $color="#065f46">Live</StatusChip>
                      ) : editingRow.status === 'discontinued' ? (
                        <StatusChip $bg="#fef9c3" $color="#854d0e">Paused</StatusChip>
                      ) : null}
                    </StatusChips>
                  )}
                </WorkspaceTitleBlock>
                <ButtonSecondary type="button" onClick={closeWorkspace}>
                  Close
                </ButtonSecondary>
              </WorkspaceToolbar>
              <Subtitle style={{ marginTop: 0, marginBottom: '1rem', lineHeight: 1.5 }}>
                {isNewCampaignFlow
                  ? 'Draft until you publish from the list. Add phases after the first save — phase dates and fees control registration.'
                  : 'Season title, story, and banner for players. Phases below control dates and signup.'}
              </Subtitle>

              <SectionLabel id="heading-details">Season details</SectionLabel>
              <FormGrid aria-labelledby="heading-details">
                <Field $fullWidth>
                  <Label>Title *</Label>
                  <Input
                    value={form.title}
                    onChange={(e) => set('title', e.target.value)}
                    placeholder="e.g. Season 2026 – 2027"
                  />
                </Field>
                <Field $fullWidth>
                  <Label>Description</Label>
                  <TextArea
                    value={form.description}
                    onChange={(e) => set('description', e.target.value)}
                    placeholder="What players should know about this season…"
                  />
                </Field>
                <Field $fullWidth>
                  <Label>Closed registration — title</Label>
                  <Input
                    value={form.registration_closed_title}
                    onChange={(e) => set('registration_closed_title', e.target.value)}
                    placeholder="e.g. Registrations open on 17 August"
                  />
                </Field>
                <Field $fullWidth>
                  <Label>Closed registration — body</Label>
                  <TextArea
                    value={form.registration_closed_body}
                    onChange={(e) => set('registration_closed_body', e.target.value)}
                    placeholder="Message when no phase is open for registration…"
                  />
                  <Hint style={{ marginTop: '0.35rem' }}>
                    Shown when no phase is open for registration; blank = default app message.
                  </Hint>
                </Field>
                {editingId ? (
                  <Field $fullWidth>
                    <Label>Banner photo</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      disabled={uploadBannerMutation.isLoading}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        uploadBannerMutation.mutate({ id: editingId, file: f });
                        e.target.value = '';
                      }}
                      style={{ padding: '0.35rem 0' }}
                    />
                    <Hint style={{ marginTop: '0.35rem' }}>
                      {uploadBannerMutation.isLoading
                        ? 'Uploading to media bucket…'
                        : 'Shown on player season cards. Stored in Cloudflare R2; Postgres keeps the URL only.'}
                    </Hint>
                  </Field>
                ) : (
                  <Field $fullWidth>
                    <Hint>Save the draft once, then you can upload a banner photo here.</Hint>
                  </Field>
                )}
                <Field $fullWidth>
                  <Label>
                    Banner image URL <Hint>(optional)</Hint>
                  </Label>
                  <Input
                    value={form.banner_image_url}
                    onChange={(e) => set('banner_image_url', e.target.value)}
                    placeholder="https://…"
                  />
                </Field>
              </FormGrid>

              <Actions>
                <Button
                  type="button"
                  onClick={handleSave}
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                >
                  {editingId
                    ? updateMutation.isLoading
                      ? 'Saving…'
                      : editingStatus === 'draft'
                        ? 'Update draft'
                        : 'Save changes'
                    : createMutation.isLoading
                      ? 'Saving…'
                      : 'Save draft'}
                </Button>
              </Actions>

            {!editingId ? (
              <Subtitle style={{ marginTop: '1.25rem', marginBottom: 0 }}>
                Save a draft first (title and Save draft). Then create or link phases here without leaving this panel.
              </Subtitle>
            ) : (
              <div style={{ marginTop: '1.5rem' }}>
                <SeasonBlockTitle>Linked phases — where play actually lives</SeasonBlockTitle>
                <Subtitle style={{ marginBottom: '0.75rem' }}>
                  Each row is a real phase in your league. Registrations, fees, and status on the phase drive what teams experience;
                  the season groups them for marketing and player navigation.
                </Subtitle>
                {editingRow?.campaign_completed && (
                  <Subtitle style={{ marginBottom: '0.75rem', color: '#475569', background: '#f1f5f9', padding: '0.75rem 1rem', borderRadius: 8 }}>
                    This season is <strong>completed</strong> because its last linked phase has status Completed.
                    Players can still open it historically. For the next edition, create a <strong>new season</strong>
                    — do not add another phase here.
                  </Subtitle>
                )}
                <SeasonTips>
                  <strong>Best practice</strong>
                  <ul>
                    <li>
                      Set <strong>exactly one</strong> &ldquo;last&rdquo; phase, then set that phase&apos;s status to{' '}
                      <strong>Completed</strong> — the season then shows as Completed (still published for history) and
                      you start the next edition as a <strong>new season</strong>.
                    </li>
                    <li>
                      Editing a phase here changes it <strong>everywhere</strong> that phase is used — communicate before changing dates
                      mid-phase.
                    </li>
                    <li>
                      Disable in season stops this season from referencing the phase; it does not delete the phase league record.
                    </li>
                  </ul>
                </SeasonTips>

                <SectionLabel>Phases attached to this season</SectionLabel>
                {linkedSeasons.length === 0 ? (
                  <Subtitle>
                    Nothing linked yet — create a phase inside this season or attach an existing one further down when you&apos;re ready.
                  </Subtitle>
                ) : (
                  <DraftList>
                    {linkedSeasons.map((ls) => (
                      <Fragment key={ls.id}>
                        <DraftItem $interactive={false}>
                          <DraftInfo>
                            <strong>{ls.season_name}</strong>
                            {' · '}
                            <span style={{ color: '#6b7280', fontSize: '0.8125rem' }}>
                              {String(ls.start_date || ls.season_start_date || '').slice(0, 10)}
                            </span>
                            {ls.is_last_season ? (
                              <span style={{ marginLeft: '0.5rem', color: '#065f46', fontSize: '0.8125rem', fontWeight: 600 }}>
                                Last phase of season
                              </span>
                            ) : null}
                          </DraftInfo>
                          <DraftActions>
                            <ButtonSecondary
                              type="button"
                              onClick={() => {
                                if (editingLinkedSeasonId === ls.season_id) {
                                  setEditingLinkedSeasonId(null);
                                } else {
                                  setEditingLinkedSeasonId(ls.season_id);
                                  setEditSeasonForm(linkedSeasonRowToEditForm(ls));
                                }
                              }}
                            >
                              {editingLinkedSeasonId === ls.season_id ? 'Close editor' : 'Edit phase'}
                            </ButtonSecondary>
                            {!ls.is_last_season && (
                              <ButtonGoLive
                                type="button"
                                onClick={async () => {
                                  try {
                                    await adminEventTournamentsAPI.updateLinkedSeason(editingId, ls.season_id, {
                                      is_last_season: true,
                                    });
                                    queryClient.invalidateQueries(['adminEventTournamentLinkedSeasons', editingId]);
                                    queryClient.invalidateQueries(['adminEventTournamentsSeasons', tenantId]);
                                    toast.success('Marked as last phase of season.');
                                  } catch (e) {
                                    toast.error(e.response?.data?.message || e.message || 'Update failed');
                                  }
                                }}
                              >
                                Mark as last
                              </ButtonGoLive>
                            )}
                            <ButtonDanger type="button" onClick={() => handleUnlinkSeason(ls.season_id)}>
                              Disable in season
                            </ButtonDanger>
                          </DraftActions>
                        </DraftItem>
                        {editingLinkedSeasonId === ls.season_id && (
                          <li style={{ listStyle: 'none', width: '100%', margin: '0.25rem 0 0.75rem 0' }}>
                            <Card style={{ margin: 0, padding: '1rem', boxShadow: 'none', border: '1px solid #e5e7eb' }}>
                              <SectionLabel>Edit linked phase</SectionLabel>
                              <SectionHelp style={{ marginTop: 0, marginBottom: '0.75rem' }}>
                                Every field changes the <strong>phase record globally</strong>, not only inside this season wall. Status drives whether the phase looks &ldquo;live&rdquo; for publishing checks (upcoming · registration · active · completed).
                              </SectionHelp>
                              <SeasonFormFieldsShared form={editSeasonForm} setForm={setEditSeasonForm} dense />
                              <FormGrid>
                                <Field>
                                  <Label>Phase status</Label>
                                  <Select
                                    value={editSeasonForm.status || 'draft'}
                                    onChange={(e) =>
                                      setEditSeasonForm((f) => ({ ...f, status: e.target.value }))
                                    }
                                  >
                                    <option value="draft">Draft</option>
                                    <option value="upcoming">Upcoming</option>
                                    <option value="registration_open">Registration open</option>
                                    <option value="active">Active</option>
                                    <option value="completed">Completed</option>
                                  </Select>
                                </Field>
                              </FormGrid>
                              <Actions>
                                <Button
                                  type="button"
                                  onClick={handleSaveEditedLinkedSeason}
                                  disabled={updateSeasonViaCampaignMutation.isLoading}
                                >
                                  {updateSeasonViaCampaignMutation.isLoading ? 'Saving…' : 'Save phase changes'}
                                </Button>
                                <ButtonSecondary type="button" onClick={() => setEditingLinkedSeasonId(null)}>
                                  Cancel
                                </ButtonSecondary>
                              </Actions>
                            </Card>
                          </li>
                        )}
                      </Fragment>
                    ))}
                  </DraftList>
                )}

                {linkedSeasons.length >= 2 && (
                  <>
                    <SectionLabel id="heading-copy-div">Carry divisions between phases</SectionLabel>
                    <SectionHelp id="desc-copy-div" aria-labelledby="heading-copy-div">
                      Use when rolling from one championship window to another: we clone division trees and roster assignments onto the newer
                      phase&apos;s IDs so records from past phases remain untouched.
                    </SectionHelp>
                    <FormGrid style={{ alignItems: 'end' }} aria-describedby="desc-copy-div">
                      <Field>
                        <Label>Source phase</Label>
                        <Select
                          value={copySourceSeasonId}
                          onChange={(e) => setCopySourceSeasonId(e.target.value)}
                        >
                          <option value="">Select…</option>
                          {linkedSeasons.map((ls) => (
                            <option key={`src-${ls.season_id}`} value={ls.season_id}>
                              {ls.season_name} ({String(ls.start_date || '').slice(0, 10)})
                            </option>
                          ))}
                        </Select>
                      </Field>
                      <Field>
                        <Label>Target phase</Label>
                        <Select
                          value={copyTargetSeasonId}
                          onChange={(e) => setCopyTargetSeasonId(e.target.value)}
                        >
                          <option value="">Select…</option>
                          {linkedSeasons.map((ls) => (
                            <option key={`tgt-${ls.season_id}`} value={ls.season_id}>
                              {ls.season_name} ({String(ls.start_date || '').slice(0, 10)})
                            </option>
                          ))}
                        </Select>
                      </Field>
                    </FormGrid>
                    <Actions>
                      <Button
                        type="button"
                        disabled={
                          copyDivisionsMutation.isLoading ||
                          !copySourceSeasonId ||
                          !copyTargetSeasonId ||
                          copySourceSeasonId === copyTargetSeasonId
                        }
                        onClick={() =>
                          copyDivisionsMutation.mutate({
                            campaignId: editingId,
                            source_season_id: copySourceSeasonId,
                            target_season_id: copyTargetSeasonId,
                          })
                        }
                      >
                        {copyDivisionsMutation.isLoading ? 'Copying…' : 'Copy divisions & assignments'}
                      </Button>
                    </Actions>
                  </>
                )}

                {!editingRow?.campaign_completed && (
                  <>
                <SectionLabel id="heading-create-season">Create a new phase inside this season</SectionLabel>
                <SectionHelp id="desc-create-season" aria-labelledby="heading-create-season">
                  Creates a phase inside this season and enrolls campaign teams. If a previous phase already has
                  group/season-scoped divisions, those are copied automatically onto the new phase (new IDs + team
                  placements remapped). The first phase uses shared skill-band divisions; build groups in League
                  Management. Manual “Copy divisions” below remains for recovery or re-runs.
                </SectionHelp>
                <SeasonFormFieldsShared
                  form={seasonForm}
                  setForm={setSeasonForm}
                  showActivateNow
                  dense
                />
                <Actions>
                  <Button
                    type="button"
                    onClick={handleCreateSeasonInCampaign}
                    disabled={createSeasonInCampaignMutation.isLoading}
                  >
                    {createSeasonInCampaignMutation.isLoading ? 'Creating phase…' : 'Create phase in season'}
                  </Button>
                </Actions>

                <SectionLabel id="heading-attach">Attach existing tenant phases</SectionLabel>
                <SectionHelp id="desc-attach" aria-labelledby="heading-attach">
                  Tap phases that already exist in your league directory; grey pills are pinned to another season — finish that relationship first before reusing here. Save attaches your selection wholesale.
                </SectionHelp>
                <SeasonsWrap aria-describedby="desc-attach">
                  {tenantSeasons.map((s) => (
                    <SeasonPill
                      type="button"
                      key={s.id}
                      $selected={selectedSeasonIds.includes(s.id)}
                      onClick={() => {
                        const linkedElsewhere =
                          s.linked_campaign_id &&
                          String(s.linked_campaign_id) !== String(editingId) &&
                          !linkedSeasonIdSet.has(String(s.id));
                        if (linkedElsewhere) return;
                        toggleSeason(s.id);
                      }}
                      disabled={
                        !!(
                          s.linked_campaign_id &&
                          String(s.linked_campaign_id) !== String(editingId) &&
                          !linkedSeasonIdSet.has(String(s.id))
                        )
                      }
                      title={
                        s.linked_campaign_id &&
                        String(s.linked_campaign_id) !== String(editingId)
                          ? `Locked to season: ${s.linked_campaign_title || 'another season'}`
                          : ''
                      }
                    >
                      {s.name}
                      {s.linked_campaign_id && String(s.linked_campaign_id) !== String(editingId) ? ' (locked)' : ''}
                    </SeasonPill>
                  ))}
                </SeasonsWrap>
                <Actions>
                  <Button type="button" onClick={handleSaveLinkedSeasons} disabled={linkSeasonsMutation.isLoading}>
                    {linkSeasonsMutation.isLoading ? 'Saving…' : 'Save linked phases'}
                  </Button>
                </Actions>
                  </>
                )}
              </div>
            )}
            </WorkspaceCard>
          )}
        </WorkspaceColumn>
      </MainGrid>
        </>
      )}
    </Container>
  );
};

export default AdminSettingsTournaments;
